/**
 * Completion Guards
 *
 * These guards ensure users have completed necessary steps in their journey.
 * They handle business logic requirements beyond basic authentication.
 *
 * Guards:
 * - checkPremiumParentRegistration: Ensures premium parents have completed address registration
 * - checkCorporateOnboarding: Ensures corporate parents have completed onboarding
 * - checkSitterOnboarding: Ensures sitters have completed their onboarding process
 */
import { NavigationGuard } from 'vue-router';
import { redirectParentRegistration } from '@/helpers/parentRegistrationChecks';
import { getSharedApolloClient, setupApollo } from '@/lib/apollo_setup';
import { redirectIfNotOnboarded, navigateToRoute, isExternalRoute } from '@/lib/sitterOnboarding/sitterOnboardingState';
import OnboardingQueries from '@/graphql/onboarding/queries';
import store from '../../store';
import router from '../index';

export const checkPremiumParentRegistration: NavigationGuard = async (to, _from, next) => {
  const account = store.getters['account/getAccount'];

  if (account?.isPremium()) {
    try {
      const apolloClient = getSharedApolloClient();
      const redirected = await redirectParentRegistration(apolloClient, to.fullPath);
      if (redirected) {
        next(false);
        return;
      }
    } catch (error) {
      console.error('Error in redirectParentRegistration:', error);
    }
  }

  next();
};

export const checkCorporateOnboarding: NavigationGuard = async (to, _from, next) => {
  const account = store.getters['account/getAccount'];

  if (account?.isCorporate()) {
    try {
      const apolloClient = getSharedApolloClient();
      const { data } = await apolloClient.query({
        query: OnboardingQueries.FETCH_ONBOARDING_STATUS,
        fetchPolicy: 'network-only',
      });

      if (!data.memberOnboarding.complete) {
        const returnPath = to.fullPath;
        next({
          path: '/corporate-onboarding',
          query: { return_path: returnPath },
        });
        return;
      }
    } catch (error) {
      console.error('Error checking corporate onboarding:', error);
    }
  }

  next();
};

export const checkSitterOnboarding: NavigationGuard = async (_to, _from, next) => {
  const account = store.getters['account/getAccount'];

  if (account?.isSitter()) {
    try {
      const apolloProvider = setupApollo();
      const targetRoute = await redirectIfNotOnboarded(apolloProvider, account, router, _to.path, _to.query);
      if (targetRoute) {
        // Legacy (external) routes need a full-page navigation. SPA routes must redirect via
        // next(targetRoute) rather than router.push() + next(false): aborting with next(false)
        // on the app's initial navigation rejects router.isReady() and surfaces as a fatal
        // "Application Error" (e.g. an incomplete sitter landing on the dashboard after login).
        if (isExternalRoute(targetRoute)) {
          await navigateToRoute(targetRoute, router);
          next(false);
        } else {
          next(targetRoute);
        }
        return;
      }
    } catch (error) {
      console.error('Error in checkSitterOnboarding:', error);
      next('/sitter/signup/address');
      return;
    }
  }

  next();
};
